<template>
  <div class="delivery-new">
    <template v-if="!detailCorrectionStore.loading && !detailCorrectionStore.error">
      <v-container>
        <v-form v-model="formValid" :ref="form">
          <date-picker
            class="mt-5"
            v-model="date"
            label="Datum"
            :rules="[ruleRequired]"
          />
          <div class="d-flex">
            <v-btn color="primary" @click="save" :disabled="!formValid">{{
                $route.params.id ? 'Bearbeiten' : 'Erstellen'
              }}
            </v-btn>
            <v-spacer/>
            <v-btn v-if="$route.params.id" :loading="deleting" color="error" :disabled="detailCorrectionStore.data?.Erledigt"
                   @click="deleteCorrection">
              <v-icon>mdi-delete</v-icon>
              Löschen
            </v-btn>
          </div>
        </v-form>
      </v-container>
    </template>
    <template v-else-if="detailCorrectionStore.error">
      <error :module="detailCorrectionStore.$id"/>
    </template>
    <template v-else>
      <v-overlay v-model="detailCorrectionStore.loading" class="align-center justify-center" persistent>
        <v-card>
          <v-card-text class="text-center">
            <div class="mb-4">
              <v-progress-circular indeterminate/>
            </div>
            <div class="mt-4">
              Berstandskorrekturerfassung wird geladen
            </div>
          </v-card-text>
        </v-card>
      </v-overlay>
    </template>
  </div>
</template>

<script setup lang="ts">
import {nextTick, ref, VNodeRef, watch} from 'vue';
import ErrorComp from '@/components/Error.vue';
import router from '@/router';
import DatePicker from '@/components/DatePicker.vue';
import moment from 'moment';
import {doAlert, doConfirm} from '@/util';
import {useSettings} from '@/store/settings';
import Picklist, {ApiPicklistState, ApiPicklistType} from '@/model/Picklist';
import {deleteCorrection as deleteCorrectionApiCall} from '@/api/module/correction';
import {useStorage} from '@/composables/storage';
import useDetailPicklistStore from '@/store/detailPicklist';
import useSubsidiariesStore from '@/store/subsidiaries';
import useDetailCorrectionStore from '@/store/detailCorrection';
import Correction from '@/model/Correction';

const detailCorrectionStore = useDetailCorrectionStore();
const detailPicklistStore = useDetailPicklistStore();
const subsidiariesStore = useSubsidiariesStore();

const {} = useStorage(detailCorrectionStore,
    (_, route) => route?.params.id ? `Bestandskorrektur ${route.params.id} bearbeiten` : 'Bestandskorrektur erstellen',
    (route) => {
      return {BelegNr: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id)};
    });

const date = ref(moment().format('YYYY-MM-DD'));
const formValid = ref(false);
const form = ref<HTMLFormElement & VNodeRef | undefined>();
const deleting = ref(false);

watch(() => detailCorrectionStore.data, async (data: Correction|null) => {
  if (data) date.value = data.DatumParsed.format('YYYY-MM-DD');
  await nextTick();
  form.value?.validate();
});
watch(form, () => {
  form.value?.validate();
});


async function save() {
  const settingsStore = useSettings();
  try {
    const correction = detailCorrectionStore.data;
    form.value?.validate();
    if (!formValid.value) throw new Error('Bitte alle Felder korrekt ausfüllen');
    const picklist = Picklist.fromApi({
      Art: ApiPicklistType.Correction,
      Picklistnr: correction?.BelegNr ?? 0,
      BestellNr: null,
      LieferantenNr: null,
      Erstelltam: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
      Erstelltdurch: correction?.Bediener ?? 'SYS',
      Status: correction?.Erledigt ? ApiPicklistState.Done : ApiPicklistState.Todo,
      Kmsdatum: date.value,
      Kmsuhrzeit: '',
      Kmsprioritaet: '',
      Kmsam: date.value,
      Kmsdurch: '',
      Kmsbediener: useSettings().userShort ?? 'SYS',
      Tour: [],
      Auftraege: [],
      InterneAuftraege: correction?.convertedPositions ?? [],
    });

    picklist.lieferantName = subsidiariesStore.data.find((s) => s.NiederlassungNr === settingsStore.selectedSubsidiary)?.Bezeichnung;
    picklist.done = correction?.Erledigt ?? false;
    detailPicklistStore.data = picklist;

    router.push({name: 'correction_pick'}).catch(console.error);
  } catch (err: any) {
    await doAlert(err.message, 'Fehler');
  }
}

async function deleteCorrection() {
  const id = detailCorrectionStore.data?.BelegNr ?? -1;
  if (!(await doConfirm(`Bestandskorrektur ${id} wirklich löschen?`, 'Wirklich löschen?'))) return;

  try {
    deleting.value = true;
    await deleteCorrectionApiCall(id);
    await router.push({name: 'correction_list'});
  } catch (err) {
    console.error(err);
    await doAlert((err as Error).message, 'Fehler');
  } finally {
    deleting.value = false;
  }
}

function ruleRequired(val: string | null | undefined): string | true {
  if (!val) return 'erforderlich';

  return true;
}

defineExpose({
  Error: ErrorComp,
  DatePicker,
  date,
  formValid,
  form,
  deleting,
  detailCorrectionStore,
  deleteCorrection,
  ruleRequired,
  save,
});
</script>

<style>
.item-operator-info .v-chip__content {
  display: flex;
  justify-content: center;
}

.v-pagination__navigation, .v-pagination__item--active {
  box-shadow: none;
}
</style>
